import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { SendingSetup } from "../../../../helpers/sending_setup_helper";

type Props = {
  open: boolean;
  saving: boolean;
  setup: SendingSetup | null;
  fieldErrors: Record<string, string>;
  onClose: () => void;
  onSubmit: (payload: {
    tracking_hostname: string;
    sending_domain: string;
    from_email: string;
    from_name: string;
    reply_to: string | null;
  }) => void;
};

const SendingSetupFormModal = ({
  open,
  saving,
  setup,
  fieldErrors,
  onClose,
  onSubmit,
}: Props) => {
  const { t } = useTranslation();
  const isNs = setup?.dns_delegation_mode === "ns";
  const [trackingHostname, setTrackingHostname] = useState("");
  const [sendingDomain, setSendingDomain] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [fromName, setFromName] = useState("");
  const [replyTo, setReplyTo] = useState("");

  useEffect(() => {
    if (!open) return;
    setTrackingHostname(setup?.tracking_hostname || "");
    setSendingDomain(setup?.sending_domain || "");
    setFromEmail(setup?.from_email || "");
    setFromName(setup?.from_name || "");
    setReplyTo(setup?.reply_to || "");
  }, [open, setup]);

  /**
   * LAC-008: um subdomínio cobre links + domínio de envio.
   * Modo NS: tracking = sending (mesma zona delegada).
   */
  const syncFromTracking = () => {
    const host = trackingHostname.trim().toLowerCase();
    if (!host) return;

    if (isNs || !sendingDomain.trim()) {
      setSendingDomain(host);
    }

    const domain = (isNs ? host : sendingDomain.trim() || host).toLowerCase();
    if (!fromEmail.trim() || isNs) {
      const local = fromEmail.includes("@")
        ? fromEmail.split("@")[0]
        : "news";
      if (!fromEmail.trim()) {
        setFromEmail(`news@${domain}`);
      } else if (isNs) {
        setFromEmail(`${local || "news"}@${domain}`);
      }
    }
  };

  const syncFromDomain = () => {
    if (isNs) return;
    const domain = sendingDomain.trim().toLowerCase();
    if (!domain) return;
    if (!fromEmail.trim()) {
      setFromEmail(`news@${domain}`);
    }
  };

  const handleTrackingChange = (value: string) => {
    setTrackingHostname(value);
    if (isNs) {
      setSendingDomain(value);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const host = trackingHostname.trim();
    const reply = replyTo.trim();
    onSubmit({
      tracking_hostname: host,
      sending_domain: isNs ? host : sendingDomain.trim(),
      from_email: fromEmail.trim(),
      from_name: fromName.trim(),
      // vazio = limpa default; caixa apex ok (não precisa ser domínio SES).
      reply_to: reply !== "" ? reply : null,
    });
  };

  return (
    <Modal isOpen={open} toggle={onClose} centered>
      <ModalHeader toggle={onClose}>{t("settings.sending.form.title")}</ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          <p className="text-muted small mb-3">
            {isNs
              ? t("settings.sending.form.nsSingleHostHint")
              : t("settings.sending.form.singleSubdomainHint")}
          </p>

          <div className="mb-3">
            <Label htmlFor="tracking_hostname">
              {isNs
                ? t("settings.sending.form.nsHostname")
                : t("settings.sending.form.trackingHostname")}
            </Label>
            <Input
              id="tracking_hostname"
              value={trackingHostname}
              invalid={Boolean(fieldErrors.tracking_hostname)}
              onChange={(e) => handleTrackingChange(e.target.value)}
              onBlur={syncFromTracking}
              placeholder="mkt.suaempresa.com.br"
              autoComplete="off"
            />
            {fieldErrors.tracking_hostname ? (
              <FormFeedback>{fieldErrors.tracking_hostname}</FormFeedback>
            ) : (
              <div className="form-text">
                {isNs
                  ? t("settings.sending.form.nsHostnameHelp")
                  : t("settings.sending.form.trackingHostnameHelp")}
              </div>
            )}
          </div>

          {!isNs && (
            <div className="mb-3">
              <Label htmlFor="sending_domain">
                {t("settings.sending.form.sendingDomain")}
              </Label>
              <Input
                id="sending_domain"
                value={sendingDomain}
                invalid={Boolean(fieldErrors.sending_domain)}
                onChange={(e) => setSendingDomain(e.target.value)}
                onBlur={syncFromDomain}
                placeholder="email.suaempresa.com.br"
                autoComplete="off"
              />
              {fieldErrors.sending_domain ? (
                <FormFeedback>{fieldErrors.sending_domain}</FormFeedback>
              ) : (
                <div className="form-text">
                  {t("settings.sending.form.sendingDomainHelp")}
                </div>
              )}
            </div>
          )}

          {isNs && (
            <Alert color="light" className="mb-3 py-2 small">
              {t("settings.sending.form.nsDomainLocked", {
                host: trackingHostname.trim() || "mkt.suaempresa.com.br",
              })}
            </Alert>
          )}

          <div className="mb-3">
            <Label htmlFor="from_email">{t("settings.sending.form.fromEmail")}</Label>
            <Input
              id="from_email"
              type="email"
              value={fromEmail}
              invalid={Boolean(fieldErrors.from_email)}
              onChange={(e) => setFromEmail(e.target.value)}
              placeholder="news@mkt.suaempresa.com.br"
            />
            {fieldErrors.from_email ? (
              <FormFeedback>{fieldErrors.from_email}</FormFeedback>
            ) : (
              <div className="form-text">
                {t("settings.sending.form.fromEmailHelp")}
              </div>
            )}
          </div>

          <div className="mb-3">
            <Label htmlFor="from_name">{t("settings.sending.form.fromName")}</Label>
            <Input
              id="from_name"
              value={fromName}
              invalid={Boolean(fieldErrors.from_name)}
              onChange={(e) => setFromName(e.target.value)}
              placeholder="Sua Empresa"
            />
            {fieldErrors.from_name && (
              <FormFeedback>{fieldErrors.from_name}</FormFeedback>
            )}
          </div>

          <div className="mb-0">
            <Label htmlFor="reply_to">{t("settings.sending.form.replyTo")}</Label>
            <Input
              id="reply_to"
              type="email"
              value={replyTo}
              invalid={Boolean(fieldErrors.reply_to)}
              onChange={(e) => setReplyTo(e.target.value)}
              placeholder="atendimento@suaempresa.com.br"
            />
            {fieldErrors.reply_to ? (
              <FormFeedback>{fieldErrors.reply_to}</FormFeedback>
            ) : (
              <div className="form-text">
                {t("settings.sending.form.replyToHelp")}
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button type="button" color="light" onClick={onClose} disabled={saving}>
            {t("settings.sending.form.cancel")}
          </Button>
          <Button type="submit" color="primary" disabled={saving}>
            {saving && <Spinner size="sm" className="me-1" />}
            {t("settings.sending.form.save")}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default SendingSetupFormModal;
